/**
 * FreshCards - Main Application Component
 * Full Notion-class workspace with proper integration
 */

import React, { useState } from 'react';
import { WorkspaceProvider, useWorkspace } from './core/workspace/WorkspaceContext';
import { PeekManagerProvider, usePeek } from './ui/components/common/PeekManager';
import WorkspaceSurface from './ui/components/surfaces/WorkspaceSurface';
import { Box, ActionIcon, Tooltip, Group, Loader, Center, Text, Stack, TextInput, Avatar, Menu, Divider, Badge, Kbd, Button } from '@mantine/core';
import { IconSparkles, IconPlus, IconSun, IconMoon, IconSearch, IconSettings, IconLogout, IconUser, IconBell, IconQuestionMark, IconMenu2, IconChevronDown } from '@tabler/icons-react';
import { useMantineColorScheme } from '@mantine/core';
import '@mantine/core/styles.css';

/**
 * Workspace Header Component - Notion-style top bar
 */
const WorkspaceHeader: React.FC = () => {
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const { isPeekOpen, togglePeek, closePeek } = usePeek();
  const { toggleSidebar, goToHome } = useWorkspace();
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const { pages, selectPage } = useWorkspace();

  return (
    <Box
      style={{
        height: 50,
        borderBottom: colorScheme === 'dark' ? '1px solid #3c4043' : '1px solid var(--mantine-color-gray-3)',
        display: 'flex',
        alignItems: 'center',
        padding: '0 16px',
        backgroundColor: colorScheme === 'dark' ? '#202124' : '#ffffff',
        color: colorScheme === 'dark' ? '#e8eaed' : '#202124',
        gap: 12
      }}
    >
      {/* Left section - Workspace name and navigation */}
      <Group gap="xs" style={{ flex: '0 0 auto' }}>
        <ActionIcon variant="subtle" size="md" title="Menu" onClick={toggleSidebar}>
          <IconMenu2 size={18} />
        </ActionIcon>
        <Text 
          fw={600} 
          size="sm" 
          style={{ letterSpacing: '-0.01em', cursor: 'pointer' }}
          onClick={goToHome}
          title="Go to Home"
        >
          FreshCards
        </Text>
        <ActionIcon variant="subtle" size="sm" title="Workspace options" onClick={() => console.log('Workspace options clicked')}>
          <IconChevronDown size={14} />
        </ActionIcon>
      </Group>

      {/* Center section - Search */}
      <Box style={{ flex: 1, maxWidth: 600, margin: '0 auto', position: 'relative' }}>
        <TextInput
          placeholder="Search pages..."
          size="sm"
          leftSection={<IconSearch size={16} stroke={1.5} />}
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setShowSearchResults(e.target.value.length > 0);
          }}
          styles={{
            root: { width: '100%' },
            input: {
              borderRadius: 4,
              backgroundColor: colorScheme === 'dark' ? '#303134' : '#f1f3f4',
              border: searchFocused ? '1px solid #1a73e8' : '1px solid transparent',
              '&:hover': {
                backgroundColor: colorScheme === 'dark' ? '#3c4043' : '#e8eaed'
              }
            }
          }}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setTimeout(() => setShowSearchResults(false), 200)}
        />
        {showSearchResults && searchQuery && (
          <Box
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              backgroundColor: colorScheme === 'dark' ? '#303134' : '#ffffff',
              borderRadius: 4,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              maxHeight: 300,
              overflow: 'auto',
              zIndex: 1000,
              marginTop: 4
            }}
          >
            {pages
              .filter(p => p.title.toLowerCase().includes(searchQuery.toLowerCase()))
              .slice(0, 5)
              .map(page => (
                <Box
                  key={page.id}
                  p="sm"
                  style={{ cursor: 'pointer', borderBottom: '1px solid #e9e9e7' }}
                  onClick={() => {
                    setSearchQuery('');
                    setShowSearchResults(false);
                    selectPage(page.id);
                  }}
                >
                  <Text size="sm">{page.title}</Text>
                </Box>
              ))}
            {pages.filter(p => p.title.toLowerCase().includes(searchQuery.toLowerCase())).length === 0 && (
              <Box p="sm">
                <Text size="sm" c="dimmed">No pages found</Text>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Right section - Actions and user */}
      <Group gap="xs" style={{ flex: '0 0 auto' }}>
        <ActionIcon variant="subtle" size="md" title="Help" onClick={() => alert('Help: FreshCards v1.0\n\nKeyboard shortcuts:\n• Ctrl+N: New page\n• Ctrl+S: Save\n• Ctrl+/ Search')}>
          <IconQuestionMark size={18} />
        </ActionIcon>

        <ActionIcon variant="subtle" size="md" title="Notifications" onClick={() => alert('Notifications\n\nNo new notifications')}>
          <IconBell size={18} />
        </ActionIcon>

<Divider orientation="vertical" style={{ height: 24 }} />

        <ActionIcon
          variant={isPeekOpen('quick-note') ? 'filled' : 'subtle'}
          color="blue"
          onClick={() => togglePeek('quick-note')}
          size="md"
          title="Quick Note"
        >
          <IconPlus size={18} />
        </ActionIcon>

        <ActionIcon
          variant={isPeekOpen('ai-chat') ? 'filled' : 'subtle'}
          color="grape"
          onClick={() => togglePeek('ai-chat')}
          size="md"
          title="AI Chat"
        >
          <IconSparkles size={18} />
        </ActionIcon>

        <ActionIcon
          variant="subtle"
          onClick={() => toggleColorScheme()}
          size="md"
          title={colorScheme === 'dark' ? 'Light mode' : 'Dark mode'}
        >
          {colorScheme === 'dark' ? <IconSun size={18} /> : <IconMoon size={18} />}
        </ActionIcon>

        <Menu position="bottom-end" shadow="md" width={200}>
          <Menu.Target>
            <ActionIcon variant="subtle" size="md" radius="xl">
              <Avatar size={28} color="blue" radius="xl">
                U
              </Avatar>
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>Signed in as User</Menu.Label>
            <Menu.Item leftSection={<IconUser size={14} />} onClick={() => alert('Profile page coming soon!')}>
              Profile
            </Menu.Item>
            <Menu.Item leftSection={<IconSettings size={14} />} onClick={() => alert('Settings page coming soon!')}>
              Settings
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item color="red" leftSection={<IconLogout size={14} />} onClick={() => alert('Signed out successfully!')}>
              Sign out
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>
    </Box>
  );
};

/**
 * Quick Note Panel Component
 */
const QuickNotePanel: React.FC = () => {
  const [note, setNote] = useState('');
  const { createPage, selectPage } = useWorkspace();
  const { closePeek } = usePeek();

  const handleSaveNote = async (kind: 'page' | 'database' = 'page') => {
    if (!note.trim()) return;

    try {
      const newPage = await createPage(note.trim(), undefined, kind);
      await selectPage(newPage.id);
      setNote('');
      closePeek();
    } catch (error) {
      console.error('Failed to create note:', error);
    }
  };

  return (
    <Box
      style={{
        position: 'fixed',
        top: 50,
        right: 0,
        width: 320,
        height: 'calc(100vh - 50px)',
        backgroundColor: '#fff',
        boxShadow: '-2px 0 12px rgba(0,0,0,0.15)',
        display: 'flex',
        flexDirection:'column',
        zIndex: 100
      }}
    >
      <Box p="md" style={{ borderBottom: '1px solid #e9e9e7' }}>
        <Group justify="space-between" align="center">
          <Text fw={600}>Quick Note</Text>
          <ActionIcon variant="subtle" size="sm" onClick={() => closePeek()}>
            ✕
          </ActionIcon>
        </Group>
      </Box>
      <Box p="md" style={{ flex: 1 }}>
        <TextInput
          placeholder="Page or database title..."
          size="sm"
          mb="md"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSaveNote('page')}
          styles={{ input: { fontSize: 14 } }}
          autoFocus
        />
        <Text size="xs" c="dimmed">
          Type a title, then pick a kind below.
        </Text>
      </Box>
      <Box p="md" style={{ borderTop: '1px solid #e9e9e7' }}>
        <Stack gap="xs">
          <Button
            variant="filled"
            color="blue"
            fullWidth
            onClick={() => handleSaveNote('page')}
            disabled={!note.trim()}
          >
            Create Page
          </Button>
          <Button
            variant="light"
            color="grape"
            fullWidth
            onClick={() => handleSaveNote('database')}
            disabled={!note.trim()}
          >
            Create Database
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

/**
 * AI Chat Panel Component
 */
const AIChatPanel: React.FC = () => {
  const { closePeek } = usePeek();
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<Array<{role: string, content: string}>>([
    {
      role: 'assistant',
      content: 'Hi! I can help you with your documents. Try asking me to summarize the current page, answer questions about your workspace, or generate content.'
    }
  ]);

  const suggestions = [
    'Summarize this page',
    'Suggest improvements',
    'Generate a list',
    'Explain this concept'
  ];

  const handleSend = () => {
    if (!message.trim()) return;
    setMessages([...messages, { role: 'user', content: message }]);
    setMessage('');
  };

  return (
    <Box
      style={{
        position: 'fixed',
        top: 50,
        right: 0,
        width: 380,
        height: 'calc(100vh - 50px)',
        backgroundColor: '#fff',
        boxShadow: '-2px 0 12px rgba(0,0,0,0.15)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 100
      }}
    >
      <Box p="md" style={{ borderBottom: '1px solid #e9e9e7', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Group gap="sm">
          <IconSparkles size={18} color="#8b5cf6" />
          <Text fw={600}>AI Assistant</Text>
          <Badge size="xs" variant="light" color="violet">Beta</Badge>
        </Group>
        <ActionIcon variant="subtle" size="sm" onClick={() => closePeek()}>
          ✕
        </ActionIcon>
      </Box>

      {/* Messages */}
      <Box style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        <Stack gap="md">
          {messages.map((msg, i) => (
            <Box
              key={i}
              p="sm"
              style={{
                background: msg.role === 'assistant' ? '#f7f6f3' : '#e3e5f5',
                borderRadius: 8,
                maxWidth: '85%'
              }}
            >
              <Text size="sm">{msg.content}</Text>
            </Box>
          ))}
        </Stack>
      </Box>

      {/* Suggestions */}
      <Box px="md" pb="xs">
        <Text size="xs" c="dimmed" mb="xs">Try asking:</Text>
        <Group gap="xs">
          {suggestions.map((s, i) => (
            <Badge
              key={i}
              size="sm"
              variant="light"
              style={{ cursor: 'pointer' }}
              onClick={() => setMessage(s)}
            >
              {s}
            </Badge>
          ))}
        </Group>
      </Box>

      {/* Input */}
      <Box p="md" style={{ borderTop: '1px solid #e9e9e7' }}>
        <Group gap="xs">
          <Box style={{ flex: 1 }}>
            <TextInput
              placeholder="Ask AI..."
              size="sm"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              styles={{
                input: { borderRadius: 20, paddingLeft: 16, paddingRight: 16 }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && message.trim()) {
                  handleSend();
                }
              }}
            />
          </Box>
          <ActionIcon color="violet" variant="filled" size="lg" radius="xl" onClick={handleSend}>
            <IconSparkles size={16} />
          </ActionIcon>
        </Group>
      </Box>
    </Box>
  );
};

/**
 * Loading screen while workspace initializes
 */
const WorkspaceLoading: React.FC = () => (
  <Center h="100vh" w="100vw">
    <Stack align="center" gap="md">
      <Loader size="lg" />
      <Text c="dimmed" size="sm">Loading workspace...</Text>
    </Stack>
  </Center>
);

/**
 * Main workspace with header and peek panels
 */
const MainWorkspace: React.FC = () => {
  const { isPeekOpen } = usePeek();

  return (
    <Box style={{ width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <WorkspaceHeader />
      <Box style={{ flex: 1, overflow: 'hidden' }}>
        <WorkspaceSurface />
      </Box>
      {isPeekOpen('quick-note') && <QuickNotePanel />}
      {isPeekOpen('ai-chat') && <AIChatPanel />}
    </Box>
  );
};

/**
 * Full App with All Providers
 */
const FullApp: React.FC = () => {
  return (
    <WorkspaceProvider>
      <PeekManagerProvider initialPeekId={null} initialWidth={320}>
        <MainWorkspace />
      </PeekManagerProvider>
    </WorkspaceProvider>
  );
};

/**
 * FreshCards App Root
 */
const App: React.FC = () => {
  return <FullApp />;
};

export default App;